import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import { useLocation } from "react-router-dom";
import { AppBreadcrumbs } from "../components/common";

const DocumentationPage: React.FC = () => {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Fetch the README.md file
    fetch("/README.md")
      .then((response) => response.text())
      .then((text) => {
        setMarkdown(text);
        setLoading(false);

        // Scroll to the hash if present (e.g., #api)
        if (location.hash) {
          setTimeout(() => {
            const element = document.getElementById(
              location.hash.replace("#", "")
            );
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 100);
        }
      })
      .catch((error) => {
        console.error("Error loading documentation:", error);
        setLoading(false);
      });
  }, [location.hash]);

  if (loading) {
    return (
      <>
        <AppBreadcrumbs />
        <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading documentation...
          </Typography>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppBreadcrumbs />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, backgroundColor: "transparent" }}>
          <Box
            sx={{
              "& h1": {
                fontSize: "2.5rem",
                fontWeight: 700,
                mb: 2,
                color: "primary.main",
              },
              "& h2": {
                fontSize: "2rem",
                fontWeight: 600,
                mt: 4,
                mb: 2,
                borderBottom: "2px solid",
                borderColor: "divider",
                pb: 1,
              },
              "& h3": {
                fontSize: "1.5rem",
                fontWeight: 600,
                mt: 3,
                mb: 1.5,
              },
              "& h4": {
                fontSize: "1.25rem",
                fontWeight: 600,
                mt: 2,
                mb: 1,
              },
              "& p": {
                mb: 2,
                lineHeight: 1.7,
              },
              "& code": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                padding: "2px 6px",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontSize: "0.9em",
              },
              "& pre": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                mb: 2,
                "& code": {
                  backgroundColor: "transparent",
                  padding: 0,
                },
              },
              "& ul, & ol": {
                mb: 2,
                pl: 3,
              },
              "& li": {
                mb: 1,
              },
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                mb: 2,
                "& th, & td": {
                  border: "1px solid",
                  borderColor: "divider",
                  padding: "8px 12px",
                  textAlign: "left",
                },
                "& th": {
                  backgroundColor: "rgba(0, 0, 0, 0.03)",
                  fontWeight: 600,
                },
              },
              "& a": {
                color: "primary.main",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              },
              "& blockquote": {
                borderLeft: "4px solid",
                borderColor: "primary.main",
                pl: 2,
                ml: 0,
                fontStyle: "italic",
                color: "text.secondary",
              },
              "& hr": {
                my: 3,
                border: "none",
                borderTop: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default DocumentationPage;
